import { UIContainer } from "../../core/UI/UIContainer";
import { UIPositionAlign, UIAlign, UISizePercentage } from "../../core/UIHelpers";
import { Colors } from "../../utils/Colors";
import { UIText } from "../../core/UI/UIText";
import { ScrollingScoreUIElement } from "./ScrollingScoreUIElement";
import { Scene } from "../../core/Scene";
import { UIVerticalContainer } from "../../core/UI/UIVerticalContainer";
import { UITextProperties } from "../../core/UI/UIText";
import { UIElement } from "../../core/UIElement";
import { UIManager } from "../../core/UIManager";
import { ScoreManager, ScoreCategory } from "../ScoreManager";
import { GameplayLogicController } from "../GameLogicController";
import { SoundManager, SoundInstance } from "../../core/SoundManager";
import { SCORE_TEXT_FONTSIZE, SCORE_TITLE_FONTSIZE, SCORE_WIDTH } from "./UISizes";

const SCROLL_TIME = 0.5;

export class ScoreTallyUIElement extends UIVerticalContainer {
	// Properties
	private scores: {
		[k: string]: {
			nameElement: UIText,
			scoreElement: ScrollingScoreUIElement,
		}
	};

	private scene: Scene;

	// UI Elements
	private categoryContainer: UIContainer;
	private categoryNameContainer: UIVerticalContainer;
	private categoryScoreContainer: UIVerticalContainer;

	// Sounds
	private soundInstance: SoundInstance;

	constructor(scene: Scene) {
		super({
			height: new UISizePercentage(75),
			width: SCORE_WIDTH,
			x: new UIPositionAlign(UIAlign.center),
			y: new UIPositionAlign(UIAlign.center),
			padding: 5,
		});

		this.scene = scene;
	}

	protected initialize(parent: UIElement | UIManager) {
		super.initialize(parent);

		let titleContainer = new UIContainer({
			y: new UIPositionAlign(UIAlign.top),
			width: new UISizePercentage(100),
		});

		let round = GameplayLogicController.getCurrentRound() + 1;
		let inTutorial = GameplayLogicController.isInTutorial();

		let titleTextUI = new UIText(inTutorial ? ` tutorial cleared ` : ` sector ${round} cleared `, {
			x: new UIPositionAlign(UIAlign.center),
			font: "Hyperspace",
			fontSize: SCORE_TITLE_FONTSIZE,
			color: Colors.Black,
			strokeColor: Colors.Cyan,
			strokeSize: 2
		});
		titleContainer.add(titleTextUI);
		this.add(titleContainer);

		let scoreProperties: UITextProperties = {
			fontSize: SCORE_TEXT_FONTSIZE,
			font: "Hyperspace",
			color: Colors.White,
			x: new UIPositionAlign(UIAlign.right),
		};

		let totalProperties: UITextProperties = {
			fontSize: SCORE_TEXT_FONTSIZE,
			font: "Hyperspace",
			color: Colors.Cyan,
			x: new UIPositionAlign(UIAlign.right),
		};

		this.scores = {
			matterSeparated: {
				nameElement: new UIText(`matter separated (${ScoreManager.getScore(ScoreCategory.MATTER_SEPARATED).items})`, scoreProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, scoreProperties)
			},
			warpMeterBonus: {
				nameElement: new UIText(`warp meter bonus (${ScoreManager.getScore(ScoreCategory.WARP_METER_BONUS).items})`, scoreProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, scoreProperties)
			},
			researchBonus: {
				nameElement: new UIText(`research bonus (${ScoreManager.getScore(ScoreCategory.RESEARCH_BONUS).items})`, scoreProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, scoreProperties)
			},
			shieldsBonus: {
				nameElement: new UIText(`shield bonus (${ScoreManager.getScore(ScoreCategory.SHIELD_BONUS).items})`, scoreProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, scoreProperties)
			},
			sectorBonus: {
				nameElement: new UIText(`sector bonus (${ScoreManager.getScore(ScoreCategory.SECTOR_BONUS).items})`, scoreProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, scoreProperties)
			},
			total: {
				nameElement: new UIText("total", totalProperties),
				scoreElement: new ScrollingScoreUIElement(this.scene, 0, SCROLL_TIME, totalProperties)
			},
		};

		// Create containers
		this.categoryContainer = this.add(new UIContainer({
			// x: new UIPositionAlign(UIAlign.right, 200),
			width: new UISizePercentage(100),
			height: new UISizePercentage(100),
		}));

		this.categoryNameContainer = this.categoryContainer.add(new UIVerticalContainer({
			x: new UIPositionAlign(UIAlign.left, 5),
			width: new UISizePercentage(75),
			height: new UISizePercentage(100),
			fill: true,
			padding: 5,
		}));

		this.categoryScoreContainer = this.categoryContainer.add(new UIVerticalContainer({
			x: new UIPositionAlign(UIAlign.right, 5),
			width: new UISizePercentage(20),
			height: new UISizePercentage(100),
			fill: true,
			padding: 5,
		}));

		let keys = Object.keys(this.scores);

		for (let key of keys) {
			let obj = this.scores[key];

			let nameContainer = new UIContainer();
			nameContainer.add(obj.nameElement);
			this.categoryNameContainer.add(nameContainer);
			this.categoryScoreContainer.add(obj.scoreElement);
		}
	}

	private async playTallySound() {
		let tallySound = SoundManager.Tally.play();
		tallySound.loop(true);
		await this.scene.eventSystem.timeout(SCROLL_TIME * 1000);
		tallySound.stop();
	}

	public async runScores() {
		let es = this.scene.eventSystem;

		let scrollTime = SCROLL_TIME * 1000;

		let score = ScoreManager.getScore(ScoreCategory.MATTER_SEPARATED).score;
		this.scores.matterSeparated.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}

		score = ScoreManager.getScore(ScoreCategory.WARP_METER_BONUS).score;
		this.scores.warpMeterBonus.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}

		score = ScoreManager.getScore(ScoreCategory.RESEARCH_BONUS).score;
		this.scores.researchBonus.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}

		score = ScoreManager.getScore(ScoreCategory.SHIELD_BONUS).score;
		this.scores.shieldsBonus.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}

		score = ScoreManager.getScore(ScoreCategory.SECTOR_BONUS).score;
		this.scores.sectorBonus.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}

		score = ScoreManager.getCategoryTotalScore();
		this.scores.total.scoreElement.setScore(score);
		if (score !== 0) {
			this.soundInstance = SoundManager.Tally.play();
			this.soundInstance.loop(true);
			await es.timeout(scrollTime);
			this.soundInstance.stop();
		}
	}
}
